import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { withStyles } from '@material-ui/core/styles';

import CopyButton from 'components/CopyButton';
import * as api from 'api';

const styles = theme => ({
  root: {
    height: '100%',
    overflowY: 'auto',
  },
  hint: {
    color: theme.palette.text.secondary,
    marginBottom: 12,
  },
  form: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  name: {
    width: 240,
  },
  days: {
    width: 90,
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: 12,
    wordBreak: 'break-all',
  },
  secret: {
    fontFamily: 'monospace',
    fontSize: 13,
    padding: 10,
    background: theme.palette.grey[100],
    borderRadius: 4,
    wordBreak: 'break-all',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  snippet: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    padding: 10,
    background: theme.palette.grey[100],
    borderRadius: 4,
    marginTop: 8,
  },
  revoked: {
    color: theme.palette.text.disabled,
  },
});

const DEFAULT_DAYS = 90;

const mcpUrl = () => `${window.location.origin}/mcp`;

// How to hand the token to Claude Code; other MCP clients take the same URL
// and header.
const claudeCommand = token =>
  `claude mcp add --transport http pibox-admin ${mcpUrl()} \\\n` +
  `  --header "Authorization: Bearer ${token}"`;

const when = v => (v ? String(v).replace('T', ' ') : '—');

class ApiTokens extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tokens: [],
      loading: false,
      name: '',
      days: DEFAULT_DAYS,
      creating: false,
      // The token just created: shown once, in a dialog
      created: null,
      revoking: null,
    };
  }

  componentDidUpdate(prev) {
    const shown = this.props.tabValue === this.props.tabIndex;
    if (shown && prev.tabValue !== this.props.tabValue) this.load();
  }

  notifyErr = err =>
    this.props.addNotification({
      message: `Request failed: ${
        typeof err === 'string' ? err : JSON.stringify(err)
      }`,
      type: 'error',
    });

  load = () => {
    this.setState({ loading: true });
    api
      .getApiTokens()
      .then(tokens =>
        this.setState({
          loading: false,
          tokens: Array.isArray(tokens) ? tokens : [],
        })
      )
      .catch(err => {
        this.setState({ loading: false });
        this.notifyErr(err);
      });
  };

  onCreate = () => {
    const name = this.state.name.trim();
    const days = Number(this.state.days);
    if (!name) return;
    this.setState({ creating: true });
    api
      .createApiToken({ name, days })
      .then(created => {
        this.setState({
          creating: false,
          created,
          name: '',
          days: DEFAULT_DAYS,
        });
        this.load();
      })
      .catch(err => {
        this.setState({ creating: false });
        this.notifyErr(err);
      });
  };

  onRevoke = token => {
    this.setState({ revoking: token.id });
    api
      .revokeApiToken({ id: token.id })
      .then(() => {
        this.setState({ revoking: null });
        this.props.addNotification({
          message: `Revoked "${token.name}"`,
          type: 'success',
        });
        this.load();
      })
      .catch(err => {
        this.setState({ revoking: null });
        this.notifyErr(err);
      });
  };

  renderCreatedDialog = () => {
    const { classes } = this.props;
    const { created } = this.state;
    if (!created) return null;
    return (
      <Dialog open fullWidth maxWidth="sm">
        <DialogTitle>Token "{created.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.hint}>
            Copy it now: it is shown only once. It expires on{' '}
            {when(created.date_expiration)}.
          </Typography>
          <div className={classes.secret}>
            <span>{created.token}</span>
            <CopyButton value={created.token} />
          </div>
          <Typography variant="body2" style={{ marginTop: 16 }}>
            For Claude Code:
          </Typography>
          <div className={classes.snippet}>{claudeCommand(created.token)}</div>
          <Box mt={1}>
            <CopyButton value={claudeCommand(created.token)} /> copy the command
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => this.setState({ created: null })}
            color="primary"
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  render() {
    const { classes } = this.props;
    const { tokens, name, days, creating, revoking } = this.state;
    return (
      <div
        role="tabpanel"
        className={classes.root}
        hidden={this.props.tabValue !== this.props.tabIndex}
      >
        <Box p={2}>
          <Typography variant="body2" className={classes.hint}>
            A personal token lets an MCP client (Claude Code, Claude Desktop)
            use this admin panel as you: everything it does is logged under your
            account. The MCP server is at{' '}
            <span className={classes.mono}>{mcpUrl()}</span>. A token is shown
            once, expires (90 days by default, a year at most) and can be
            revoked here. You get a Telegram message when one is created.
          </Typography>

          <div className={classes.form}>
            <TextField
              className={classes.name}
              label="Name (where it is used)"
              value={name}
              onChange={e => this.setState({ name: e.target.value })}
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              className={classes.days}
              label="Days"
              type="number"
              value={days}
              onChange={e => this.setState({ days: e.target.value })}
              inputProps={{ min: 1, max: 365 }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={this.onCreate}
              disabled={creating || !name.trim()}
            >
              Create token
            </Button>
          </div>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Starts with</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map(t => {
                const dead = !!t.revoked_at;
                return (
                  <TableRow key={t.id} className={dead ? classes.revoked : ''}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell className={classes.mono}>{t.prefix}…</TableCell>
                    <TableCell>{when(t.date_created)}</TableCell>
                    <TableCell>{when(t.date_expiration)}</TableCell>
                    <TableCell>{when(t.last_used_at)}</TableCell>
                    <TableCell align="right">
                      {dead ? (
                        `revoked ${when(t.revoked_at)}`
                      ) : (
                        <Button
                          size="small"
                          color="secondary"
                          onClick={() => this.onRevoke(t)}
                          disabled={revoking === t.id}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {tokens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No tokens yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        {this.renderCreatedDialog()}
      </div>
    );
  }
}

export default withStyles(styles)(ApiTokens);
